// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import Accordion from "@insite/mobius/Accordion";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection/AccordionSection";
import Icon from "@insite/mobius/Icon";
import Truck from "@insite/mobius/Icons/Truck";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
// import getColor from "@insite/mobius/utilities/getColor"; // Greif
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import { AccordionSectionHeaderProps } from "@store/MobiusOverrides/AccordionSection/AccordionSectionHeader";
import { accordionFilterSectionProps } from "@store/Themes/common/props/accordionFilter";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
// import ProductFuturPrice from "@store/Library/Components/ProductDetails/ProductFuturePrice"; // Greif
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React from "react";
import { css } from "styled-components";

/* Greif Custom Code - Start */
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";

const ProductDescription = getWidgetComponent("ProductDetails/ProductDetailsDescription");
const ProductDetailsAddToCartOptionsAndButton = getWidgetComponent(
    "ProductDetails/ProductDetailsAddToCartOptionsAndButton",
);
const ProductDetailsPrice = getWidgetComponent("ProductDetails/ProductDetailsPrice");
const ProductDetailsDeliveryTerms = getWidgetComponent("ProductDetails/ProductDetailsDeliveryTerms");
const ProductDetailsFuturePrice = getWidgetComponent("ProductDetails/ProductDetailsFuturePrice");
const ProductDetailsAddToListLink = getWidgetComponent("ProductDetails/ProductDetailsAddToListLink");
const ProductDetailsShareLink = getWidgetComponent("ProductDetails/ProductDetailsShareLink");
const ProductDetailsLeadTime = getWidgetComponent("ProductDetails/ProductDetailsLeadTime");
const ProductDetailsFullTruckLoad = getWidgetComponent("ProductDetails/ProductDetailsFullTruckLoad");
const ProductDetailsShipsFrom = getWidgetComponent("ProductDetails/ProductDetailsShipsFrom");

interface OwnProps {
    termsAndConditionsLink?: LinkFieldValue;
    overrideLinkLabel?: string;
}

type Props = OwnProps & HasProduct;
/* Greif Custom Code - End */

export interface StoreProductDetailsInformationsStyle {
    componentWrapper?: InjectableCss;
    productName?: InjectableCss;
    productDetails?: InjectableCss;
    productListAndShareContainer?: InjectableCss;
    productListAndShareGridItem?: InjectableCss;
    additionalInfosWrapper?: InjectableCss;
    additionalInfosTitle?: TypographyPresentationProps;
    // Greif
    priceAndTermsWrapper?: InjectableCss;
}

const styles: StoreProductDetailsInformationsStyle = {
    componentWrapper: {
        css: css`
            max-width: 100%;
            display: flex;
            flex-direction: column;
        `,
    },
    productDetails: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            ${x => spacingStyles(pxToRem(16), x.theme.design.product.detail.contentSpacing)}
            &:first-child {
                & > section {
                    margin-bottom: ${pxToRem(15)};
                    div > span {
                        font-weight: bold;
                    }
                    span {
                        font-size: ${pxToRem(24)};
                    }
                }
            }
        `,
    },
    productName: {
        css: css`
            ${({ theme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            max-width: ${pxToRem(425)};
                        `,
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    productListAndShareContainer: {
        css: css`
            display: flex;
            flex-direction: row;
            width: 100%;
        `,
    },
    productListAndShareGridItem: {
        css: css`
            width: ${pxToRem(95)};
            & button {
                float: left;
            }
            & > div {
                ${breakpoint("lg", "up")} {
                    margin-top: 0;
                }
            }
        `,
    },
    additionalInfosWrapper: {
        css: css`
            border-top: 1px solid ${getColor("common.border")};
            border-bottom: 1px solid ${getColor("common.border")};
            margin-top: ${pxToRem(16)};
            padding: ${pxToRem(16)} 0;
        `,
    },
    additionalInfosTitle: {
        forwardAs: "h2",
        variant: "h4",
        weight: "bold",
        css: css`
            margin-bottom: ${pxToRem(10)};
        `,
    },
    // Greif
    priceAndTermsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            gap: ${pxToRem(12)};
            margin-bottom: ${pxToRem(15)};

            &:first-child {
                & > section {
                    div > span {
                        font-weight: bold;
                    }
                    span {
                        font-size: ${pxToRem(24)};
                    }
                }
            }

            ${breakpoint("xs")} {
                &&:first-child {
                    width: 100%;
                }

                &&:nth-child(2) {
                    width: 100%;
                    margin-top: ${pxToRem(8)};
                }
            }

            ${breakpoint("md")} {
                flex-direction: row;

                &&:first-child {
                    width: auto;
                }

                &&:nth-child(2) {
                    width: auto;
                    margin-top: unset;
                }
            }
        `,
    },
};

const ProductDetailsInformations: React.FC<Props> = ({ termsAndConditionsLink, overrideLinkLabel } /* Greif */) => {
    return (
        <StyledWrapper {...styles.componentWrapper}>
            <StyledWrapper {...styles.productDetails}>
                {/* Greif Custom Code - Start */}
                <StyledWrapper {...styles.priceAndTermsWrapper}>
                    <ProductDetailsPrice />
                    <ProductDetailsDeliveryTerms
                        termsAndConditionsLink={termsAndConditionsLink}
                        overrideLinkLabel={overrideLinkLabel}
                    />
                </StyledWrapper>
                {/* Greif Custom Code - End */}
                <ProductDetailsFuturePrice /> {/* Greif Custom Code */}
                <ProductDescription />
            </StyledWrapper>
            <StyledWrapper {...styles.productDetails}>
                <ProductDetailsAddToCartOptionsAndButton />
            </StyledWrapper>
            {/* Greif Custom Code - Start */}
            <StyledWrapper {...styles.productListAndShareGridItem}>
                <ProductDetailsAddToListLink />
            </StyledWrapper>
            <StyledWrapper {...styles.additionalInfosWrapper}>
                <Typography {...styles.additionalInfosTitle}>{translate("Product Fulfillment Information")}</Typography>
                <ProductDetailsShipsFrom />
                <ProductDetailsFullTruckLoad />
                <ProductDetailsLeadTime />
            </StyledWrapper>
            {/* Greif Custom Code - End */}
        </StyledWrapper>
    );
};

export default withProduct(ProductDetailsInformations);
